
import { useState } from "react";
import { useAccount, usePublicClient, useWalletClient } from "wagmi";
import { parseEther } from "viem";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { validateReferralCode } from "@/utils/referralSystem";
import { useNetworkSwitch } from "./hooks/useNetworkSwitch";
import { useContractDeployment } from "./hooks/useContractDeployment";
import { supabaseExtended } from "@/integrations/supabase/extendedClient";
import { captureException } from "@/utils/sentry";
import { REFERRAL_REGISTRY_ADDRESS, referralCodeHash, referralRegistryAbi } from "@/utils/referralRegistry";
import { FAIR_LAUNCH_POOL_ADDRESS, FAIR_LAUNCH_POOL_ABI } from "@/components/coin-detail/FairLaunchPoolConfig";
import { erc20Abi } from "@/components/create-coin/form/utils/contractConfig";
import { useLaunchTrustRegistration } from "@/features/identity/useLaunchTrustRegistration";

interface FormData {
  name: string;
  symbol: string;
  initialSupply: string;
  description: string;
  referralCode?: string;
  fairLaunch?: boolean;
  fairLaunchDuration?: string;
  fairLaunchTarget?: string;
  trustEnabled?: boolean;
  founderEntityId?: string;
  entityMetadataURI?: string;
  projectMetadataURI?: string;
}

export function useDeployContract() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { address, isConnected, chain } = useAccount();
  const [isDeploying, setIsDeploying] = useState(false);
  const { ensureCorrectNetwork } = useNetworkSwitch();
  const { deployContract } = useContractDeployment();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const trustRegistration = useLaunchTrustRegistration();

  const handleDeployContract = async (formData: FormData) => {
    if (!isConnected || !address) {
      toast({
        title: "Wallet Not Connected",
        description: "Please connect your wallet to create a coin",
        variant: "destructive",
      });
      return;
    }

    const networkReady = await ensureCorrectNetwork();
    if (!networkReady) return;

    if (!formData.name || !formData.symbol || !formData.initialSupply) {
      toast({
        title: "Invalid Input",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    if (formData.fairLaunch && !FAIR_LAUNCH_POOL_ADDRESS) {
      toast({
        title: "Fair launch unavailable",
        description: "The fair-launch pool contract has not been deployed yet.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsDeploying(true);

      let referralCode = formData.referralCode ? validateReferralCode(formData.referralCode) : null;
      let referralExpiresAt: bigint | undefined;
      if (formData.referralCode && REFERRAL_REGISTRY_ADDRESS) {
        const onChainReferral = await publicClient.readContract({
          address: REFERRAL_REGISTRY_ADDRESS,
          abi: referralRegistryAbi,
          functionName: "referrals",
          args: [referralCodeHash(formData.referralCode)],
        }) as readonly [`0x${string}`, bigint, bigint, bigint, boolean, bigint, bigint];
        const [referrer, expiresAt, , , active] = onChainReferral;
        referralExpiresAt = expiresAt;
        referralCode = active && expiresAt > BigInt(Math.floor(Date.now() / 1000))
          ? { referrerAddress: referrer, isActive: true }
          : null;
      }
      
      if (formData.referralCode && !referralCode) {
        toast({
          title: "Invalid Referral Code",
          description: "The provided referral code is invalid or inactive",
          variant: "destructive",
        });
        return;
      }

      const trustProject = await trustRegistration.registerBeforeLaunch({
        enabled: formData.trustEnabled,
        entityId: formData.founderEntityId,
        entityType: 1,
        entityMetadataURI: formData.entityMetadataURI,
        projectMetadataURI: formData.projectMetadataURI,
        projectKind: 1,
      });

      const hash = await deployContract(formData, address, referralCode);

      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      
      // Store coin data in Supabase
      if (receipt.contractAddress) {
        if (trustProject) {
          await trustRegistration.bindLaunchAsset(trustProject.projectId, receipt.contractAddress);
        }
        let launchPoolTransaction: `0x${string}` | undefined;
        if (formData.fairLaunch && FAIR_LAUNCH_POOL_ADDRESS && walletClient) {
          const tokenSupply = parseEther(formData.initialSupply);
          const approvalHash = await walletClient.writeContract({
            address: receipt.contractAddress,
            abi: erc20Abi,
            functionName: "approve",
            args: [FAIR_LAUNCH_POOL_ADDRESS, tokenSupply],
          });
          await publicClient.waitForTransactionReceipt({ hash: approvalHash });

          const createPoolHash = await walletClient.writeContract({
            address: FAIR_LAUNCH_POOL_ADDRESS,
            abi: FAIR_LAUNCH_POOL_ABI,
            functionName: "createPool",
            args: [receipt.contractAddress, tokenSupply, parseEther("0.01"), parseEther("0.001"), BigInt(formData.fairLaunchDuration || "86400"), parseEther(formData.fairLaunchTarget || "20")],
          });
          await publicClient.waitForTransactionReceipt({ hash: createPoolHash });

          launchPoolTransaction = await walletClient.writeContract({
            address: FAIR_LAUNCH_POOL_ADDRESS,
            abi: FAIR_LAUNCH_POOL_ABI,
            functionName: "depositTokens",
            args: [receipt.contractAddress],
          });
          await publicClient.waitForTransactionReceipt({ hash: launchPoolTransaction });
          toast({ title: "Fair launch listed", description: "Your full token supply is locked in the launch pool and the token is now listed." });
        }
        if (formData.referralCode && REFERRAL_REGISTRY_ADDRESS && walletClient) {
          const codeHash = referralCodeHash(formData.referralCode);
          const attributionHash = await walletClient.writeContract({
            address: REFERRAL_REGISTRY_ADDRESS,
            abi: referralRegistryAbi,
            functionName: "attributeToken",
            args: [receipt.contractAddress, codeHash],
          });
          await publicClient.waitForTransactionReceipt({ hash: attributionHash });
          await (supabaseExtended as any).from("referral_issuances").insert({
            code_hash: codeHash,
            referrer_address: referralCode?.referrerAddress,
            issuer_address: address,
            token_address: receipt.contractAddress,
            expires_at: referralExpiresAt ? new Date(Number(referralExpiresAt) * 1000).toISOString() : new Date().toISOString(),
            transaction_hash: attributionHash,
          });
        }
        try {
          const coinId = receipt.contractAddress.toLowerCase();
          const { error } = await (supabaseExtended as any).from('created_coins').upsert({
            id: coinId,
            name: formData.name,
            symbol: formData.symbol,
            description: formData.description,
            contract_address: receipt.contractAddress,
            creator_address: address.toLowerCase(),
            initial_supply: formData.initialSupply,
            transaction_hash: hash,
            fair_launch: Boolean(formData.fairLaunch),
            launch_pool_address: formData.fairLaunch ? FAIR_LAUNCH_POOL_ADDRESS : null,
            launch_pool_transaction: launchPoolTransaction || null,
          }, { onConflict: 'contract_address' });
          if (error) {
            console.error('Error storing created coin:', { message: error.message, code: error.code, details: error.details, hint: error.hint });
            captureException(error, { component: 'useDeployContract', operation: 'created_coins.upsert', contractAddress: receipt.contractAddress });
            toast({ title: "Coin deployed, listing failed", description: error.message, variant: "destructive" });
          }
        } catch (dbError) {
          console.error('Error saving created coin:', dbError);
        }
        if (formData.fairLaunch && FAIR_LAUNCH_POOL_ADDRESS && launchPoolTransaction) {
          try {
          const coinId = receipt.contractAddress.toLowerCase();
          
          const { error } = await (supabaseExtended as any).from('fair_launch_coins').upsert({
            id: coinId,
            name: formData.name,
            symbol: formData.symbol,
            description: formData.description,
            contract_address: receipt.contractAddress,
            creator_address: address.toLowerCase(),
            initial_supply: formData.initialSupply,
            transaction_hash: hash,
            fair_launch: true,
            launch_pool_address: FAIR_LAUNCH_POOL_ADDRESS,
            launch_pool_transaction: launchPoolTransaction || null,
            created_at: new Date().toISOString()
          });
          
          if (error) {
            console.error("Error storing fair-launch coin:", { message: error.message, code: error.code, details: error.details, hint: error.hint });
            captureException(error, {
              component: "useDeployContract",
              operation: "supabase.insert",
              formData: {
                coinId,
                name: formData.name,
                symbol: formData.symbol,
              },
              contractAddress: receipt.contractAddress,
            });
          }
          } catch (dbError) {
          console.error("Error saving to database:", dbError);
          captureException(dbError, {
            component: "useDeployContract",
            operation: "database.save",
            contractAddress: receipt.contractAddress,
          });
          }
        }
      }

      toast({
        title: "Coin Created Successfully!",
        description: "Contract deployed at: " + receipt.contractAddress,
      });

      navigate("/");
    } catch (error) {
      console.error("Error deploying contract:", error);
      captureException(error, {
        component: "useDeployContract",
        operation: "contract.deploy",
        formData: {
          name: formData.name,
          symbol: formData.symbol,
          initialSupply: formData.initialSupply,
        },
        userAddress: address,
        chainId: chain?.id,
      });
      
      toast({
        title: "Error Creating Coin",
        description:
          error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsDeploying(false);
    }
  };

  return {
    isDeploying,
    deployContract: handleDeployContract,
  };
}
